/** Button (U5): primary / secondary / ghost / danger, three sizes, loading. */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx } from './cx';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ControlSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ControlSize;
  /** Shows a spinner and disables the button; keeps width stable. */
  loading?: boolean;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  full?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    iconStart,
    iconEnd,
    full = false,
    className,
    children,
    disabled,
    type,
    ...rest
  },
  ref,
): JSX.Element {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cx('bl-btn', `bl-btn--${variant}`, `bl-btn--${size}`, full && 'bl-btn--full', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <Spinner />
      ) : iconStart ? (
        <span className="bl-btn__icon">{iconStart}</span>
      ) : null}
      {children != null && <span className="bl-btn__label">{children}</span>}
      {iconEnd && !loading && <span className="bl-btn__icon">{iconEnd}</span>}
    </button>
  );
});
